import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiCheck, FiX } from "react-icons/fi";

import "./HomeWPPFinalCTA.css";

export default function HomeWPPFinalCTA() {
  const { t } = useTranslation("home");

  const sectionRef = useRef(null);
  const nameInputRef = useRef(null);

  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        setIsVisible(true);
        observer.disconnect();
      },
      {
        threshold: 0.3,
      },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  const openModal = () => {
    setSubmitStatus("idle");
    setErrorMessage("");
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    setIsModalOpen(false);

    window.setTimeout(() => {
      setSubmitStatus("idle");
      setErrorMessage("");
    }, 250);
  }, [isSubmitting]);

  useEffect(() => {
    if (!isModalOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      nameInputRef.current?.focus();
    }, 150);

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSubmitting) {
        closeModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.clearTimeout(focusTimer);

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen, isSubmitting, closeModal]);

  const resetForm = () => {
    setName("");
    setEmail("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedName = name.trim();
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedName || !normalizedEmail) {
      setErrorMessage("Completá tu nombre y tu email.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setSubmitStatus("idle");

    try {
      const response = await fetch("/.netlify/functions/beta-signup", {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          name: normalizedName,
          email: normalizedEmail,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "No pudimos guardar tus datos.");
      }

      resetForm();
      setSubmitStatus("success");
    } catch (error) {
      console.error("Beta signup error:", error);

      setSubmitStatus("error");

      setErrorMessage(
        "No pudimos anotarte en este momento. Intentá nuevamente.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section
        ref={sectionRef}
        className={["home-wpp-final-cta", isVisible ? "is-visible" : ""]
          .filter(Boolean)
          .join(" ")}
        aria-labelledby="home-wpp-final-cta-title"
        aria-label={t("finalCTA.accessibility.sectionLabel")}
      >
        <div className="home-wpp-final-cta__glow" aria-hidden="true" />

        <div className="home-wpp-final-cta__inner">
          <h2 id="home-wpp-final-cta-title">
            {t("finalCTA.title.line1Before")}{" "}
            <span>{t("finalCTA.title.highlight")}</span>
            <br />
            {t("finalCTA.title.line2")}
          </h2>

          <button
            type="button"
            className="home-wpp-final-cta__button"
            aria-label={t("finalCTA.accessibility.buttonLabel")}
            onClick={openModal}
          >
            <span>{t("finalCTA.button")}</span>

            <FiArrowRight aria-hidden="true" />
          </button>
        </div>
      </section>

      {isModalOpen ? (
        <div
          className="beta-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isSubmitting) {
              closeModal();
            }
          }}
        >
          <div
            className="beta-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="beta-modal-title"
          >
            <button
              type="button"
              className="beta-modal__close"
              aria-label="Cerrar"
              onClick={closeModal}
              disabled={isSubmitting}
            >
              <FiX />
            </button>

            {submitStatus === "success" ? (
              <div className="beta-modal__success">
                <div className="beta-modal__success-icon">
                  <FiCheck />
                </div>

                <span>Ya estás adentro.</span>

                <h3 id="beta-modal-title">¡Te anotaste a la beta!</h3>

                <p>
                  Guardamos tus datos correctamente. Te vamos a contactar cuando
                  tengamos novedades.
                </p>

                <button
                  type="button"
                  className="beta-modal__primary-button"
                  onClick={closeModal}
                >
                  Perfecto
                </button>
              </div>
            ) : (
              <>
                <div className="beta-modal__heading">
                  <span>Beta ZY</span>

                  <h3 id="beta-modal-title">Sumate a los primeros.</h3>

                  <p>
                    Dejanos tus datos y te avisamos cuando puedas probar ZY.
                  </p>
                </div>

                <form className="beta-modal__form" onSubmit={handleSubmit}>
                  <label>
                    <span>Nombre</span>

                    <input
                      ref={nameInputRef}
                      type="text"
                      name="name"
                      value={name}
                      placeholder="Tu nombre"
                      autoComplete="name"
                      maxLength={80}
                      disabled={isSubmitting}
                      onChange={(event) => {
                        setName(event.target.value);

                        if (errorMessage) {
                          setErrorMessage("");
                        }
                      }}
                      required
                    />
                  </label>

                  <label>
                    <span>Email</span>

                    <input
                      type="email"
                      name="email"
                      value={email}
                      placeholder="nombre@email.com"
                      autoComplete="email"
                      maxLength={150}
                      disabled={isSubmitting}
                      onChange={(event) => {
                        setEmail(event.target.value);

                        if (errorMessage) {
                          setErrorMessage("");
                        }
                      }}
                      required
                    />
                  </label>

                  {errorMessage ? (
                    <p className="beta-modal__error" role="alert">
                      {errorMessage}
                    </p>
                  ) : null}

                  <button
                    type="submit"
                    className="beta-modal__primary-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Guardando..." : "Quiero sumarme"}
                  </button>
                </form>

                <p className="beta-modal__privacy">
                  Solo vamos a usar estos datos para contactarte sobre la beta
                  de ZY.
                </p>
              </>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
